import { useState, useEffect } from "react";
import { Copy, GripVertical, Trash2 } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ROOM_CATEGORY_LABELS,
  ROOM_LAYOUT_LABELS,
  ROOM_STYLE_LABELS,
} from "@postnatal-care/shared-types";
import {
  useCopyRoom,
  useDeleteRoom,
  useUpdateRoom,
  type RoomTypeEntity,
  RoomCategory,
  RoomLayoutType,
  RoomStyle,
} from "@/hooks/useRooms";
import { RoomPhotosPicker } from "./RoomPhotosPicker";
import { getRoomDurationPrices } from "@/utils/roomPricing";
import { SimpleDescEditor } from "../shared/SimpleDescEditor";

interface BusinessPhoto {
  id: string;
  url: string;
  isCover: boolean;
}

interface Props {
  room: RoomTypeEntity;
  businessId: string;
  businessPhotos: BusinessPhoto[];
}

const CATEGORIES = [
  RoomCategory.STANDARD,
  RoomCategory.DELUXE,
  RoomCategory.PREMIUM,
  RoomCategory.SUITE,
  RoomCategory.LUXURY,
  RoomCategory.VIP,
  RoomCategory.SHARED,
  RoomCategory.PRIVATE,
  RoomCategory.FAMILY_SUITE,
];

const LAYOUT_TYPES = [
  RoomLayoutType.STUDIO,
  RoomLayoutType.ONE_RK,
  RoomLayoutType.ONE_BHK,
  RoomLayoutType.TWO_BHK,
  RoomLayoutType.THREE_BHK,
  RoomLayoutType.DORMITORY,
];

const STYLES = [
  RoomStyle.PRIVATE,
  RoomStyle.SHARED,
  RoomStyle.FAMILY,
  RoomStyle.APARTMENT,
  RoomStyle.VILLA,
  RoomStyle.COTTAGE,
];

const normalizeCategory = (cat?: string | null): RoomCategory => {
  if (!cat) return RoomCategory.DELUXE;
  const norm = cat.trim().toUpperCase().replace(/\s+/g, "_");
  return (Object.values(RoomCategory) as string[]).includes(norm)
    ? (norm as RoomCategory)
    : RoomCategory.DELUXE;
};

const normalizeLayout = (lt?: string | null): RoomLayoutType | null => {
  if (!lt) return null;
  const trimmed = lt.trim().toUpperCase();
  if (trimmed === "1 BHK" || trimmed === "1_BHK" || trimmed === "ONE_BHK")
    return RoomLayoutType.ONE_BHK;
  if (trimmed === "1 RK" || trimmed === "1_RK" || trimmed === "ONE_RK")
    return RoomLayoutType.ONE_RK;
  if (trimmed === "2 BHK" || trimmed === "2_BHK" || trimmed === "TWO_BHK")
    return RoomLayoutType.TWO_BHK;
  if (trimmed === "3 BHK" || trimmed === "3_BHK" || trimmed === "THREE_BHK")
    return RoomLayoutType.THREE_BHK;
  const norm = trimmed.replace(/\s+/g, "_");
  return (Object.values(RoomLayoutType) as string[]).includes(norm)
    ? (norm as RoomLayoutType)
    : null;
};

const normalizeStyle = (st?: string | null): RoomStyle => {
  if (!st) return RoomStyle.PRIVATE;
  const norm = st.trim().toUpperCase();
  return (Object.values(RoomStyle) as string[]).includes(norm)
    ? (norm as RoomStyle)
    : RoomStyle.PRIVATE;
};

export const RoomTypeCard = ({ room, businessId, businessPhotos }: Props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: room.id });
  const updateMut = useUpdateRoom(businessId);
  const deleteMut = useDeleteRoom(businessId);
  const copyMut = useCopyRoom(businessId);
  const [form, setForm] = useState({
    name: room.name,
    category: normalizeCategory(room.category),
    layoutType: normalizeLayout(room.layoutType),
    occupancy: room.occupancy ?? "",
    style: normalizeStyle(room.style),
    bedroomCount: room.bedroomCount ?? 1,
    bathroomCount: room.bathroomCount ?? 1,
    hasLivingRoom: room.hasLivingRoom ?? false,
    hasKitchen: room.hasKitchen ?? false,
    hasSharedKitchen: room.hasSharedKitchen ?? false,
    hasCommonBathroom: room.hasCommonBathroom ?? false,
    hasDiningArea: room.hasDiningArea ?? false,
    hasBalcony: room.hasBalcony ?? false,
    hasAc: room.hasAc,
    hasMassageRoom: room.hasMassageRoom,
    sqftArea: room.sqftArea ?? "",
    floor: room.floor ?? "",
    totalRooms: room.totalRooms,
    description: room.description ?? "",
    durationPrices: getRoomDurationPrices(room),
  });

  useEffect(() => {
    setForm({
      name: room.name,
      category: normalizeCategory(room.category),
      layoutType: normalizeLayout(room.layoutType),
      occupancy: room.occupancy ?? "",
      style: normalizeStyle(room.style),
      bedroomCount: room.bedroomCount ?? 1,
      bathroomCount: room.bathroomCount ?? 1,
      hasLivingRoom: room.hasLivingRoom ?? false,
      hasKitchen: room.hasKitchen ?? false,
      hasSharedKitchen: room.hasSharedKitchen ?? false,
      hasCommonBathroom: room.hasCommonBathroom ?? false,
      hasDiningArea: room.hasDiningArea ?? false,
      hasBalcony: room.hasBalcony ?? false,
      hasAc: room.hasAc,
      hasMassageRoom: room.hasMassageRoom,
      sqftArea: room.sqftArea ?? "",
      floor: room.floor ?? "",
      totalRooms: room.totalRooms,
      description: room.description ?? "",
      durationPrices: getRoomDurationPrices(room),
    });
  }, [room]);
  const [dirty, setDirty] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const set = <K extends keyof typeof form>(
    key: K,
    value: (typeof form)[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const setDurationPrice = (
    index: number,
    key: "durationNights" | "price" | "nonAcPrice",
    value: number,
  ) => {
    set(
      "durationPrices",
      form.durationPrices.map((item, idx) =>
        idx === index ? { ...item, [key]: value } : item,
      ),
    );
  };

  const addDurationPrice = () => {
    set("durationPrices", [
      ...form.durationPrices,
      { durationNights: 7, price: 0, nonAcPrice: 0 },
    ]);
  };

  const removeDurationPrice = (index: number) => {
    set(
      "durationPrices",
      form.durationPrices.filter((_, idx) => idx !== index),
    );
  };

  const normalizePricing = () =>
    form.durationPrices
      .filter(
        (item) =>
          Number.isFinite(item.durationNights) &&
          item.durationNights > 0 &&
          Number.isFinite(item.price) &&
          item.price >= 0,
      )
      .map((item) => ({
        durationNights: Math.trunc(item.durationNights),
        price: Number(item.price),
        nonAcPrice:
          item.nonAcPrice !== undefined ? Number(item.nonAcPrice) : undefined,
      }))
      .sort((a, b) => a.durationNights - b.durationNights);

  const onSave = async () => {
    try {
      await updateMut.mutateAsync({
        id: room.id,
        data: {
          ...form,
          layoutType: form.layoutType || null,
          occupancy: form.occupancy.trim() || null,
          sqftArea: form.sqftArea !== "" ? Number(form.sqftArea) : null,
          durationPrices: normalizePricing(),
        },
      });
      toast.success("Room saved");
      setDirty(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
    }
  };

  const onCopy = async () => {
    try {
      await copyMut.mutateAsync(room.id);
      toast.success("Room copied as a new draft!");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to copy room");
    }
  };

  const onDelete = async () => {
    if (!confirmDel) {
      setConfirmDel(true);
      setTimeout(() => setConfirmDel(false), 3000);
      return;
    }
    try {
      await deleteMut.mutateAsync(room.id);
    } catch (e: any) {
      toast.error(e?.message ?? "Delete failed");
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="bg-card border border-border rounded-lg p-4 space-y-4"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="p-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <Input
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="Room type name"
          className="font-medium flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCopy}
          disabled={copyMut.isPending}
          title="Copy / Duplicate Room"
          className="shrink-0 gap-1 text-xs"
        >
          <Copy className="w-3.5 h-3.5" />
          Copy
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-destructive shrink-0"
          onClick={onDelete}
          disabled={deleteMut.isPending}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      {confirmDel && (
        <p className="text-xs text-destructive">
          Click delete again to confirm
        </p>
      )}

      {/* Basic Classification Fields */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">
            Room Category *
          </label>
          <Select
            value={form.category}
            onValueChange={(v) => set("category", v as RoomCategory)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {ROOM_CATEGORY_LABELS[cat] || cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">
            Layout Type (Optional)
          </label>
          <Select
            value={form.layoutType ?? "none"}
            onValueChange={(v) =>
              set("layoutType", v === "none" ? null : (v as RoomLayoutType))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="None (Standard)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None (Standard)</SelectItem>
              {LAYOUT_TYPES.map((lt) => (
                <SelectItem key={lt} value={lt}>
                  {ROOM_LAYOUT_LABELS[lt] || lt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">
            Room Style *
          </label>
          <Select
            value={form.style}
            onValueChange={(v) => set("style", v as RoomStyle)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Style" />
            </SelectTrigger>
            <SelectContent>
              {STYLES.map((st) => (
                <SelectItem key={st} value={st}>
                  {ROOM_STYLE_LABELS[st] || st}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1 sm:col-span-2">
          <label className="text-xs text-muted-foreground font-medium">
            Occupancy Label
          </label>
          <Input
            value={form.occupancy}
            onChange={(e) => set("occupancy", e.target.value)}
            placeholder="e.g. Mother + Baby + 1 Attender"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">
            Total Rooms
          </label>
          <Input
            type="number"
            min={1}
            value={form.totalRooms}
            onChange={(e) =>
              set("totalRooms", parseInt(e.target.value, 10) || 1)
            }
          />
        </div>
      </div>

      {/* Room Specifications Section */}
      <div className="rounded-md border border-border p-3 space-y-3 bg-muted/20">
        <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
          Physical Specifications
        </h4>
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground font-medium">
              Bedrooms
            </label>
            <Input
              type="number"
              min={0}
              value={form.bedroomCount}
              onChange={(e) =>
                set(
                  "bedroomCount",
                  Math.max(0, parseInt(e.target.value, 10) || 0),
                )
              }
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground font-medium">
              Bathrooms
            </label>
            <Input
              type="number"
              min={0}
              value={form.bathroomCount}
              onChange={(e) =>
                set(
                  "bathroomCount",
                  Math.max(0, parseInt(e.target.value, 10) || 0),
                )
              }
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground font-medium">
              Floor
            </label>
            <Input
              value={form.floor}
              onChange={(e) => set("floor", e.target.value)}
              placeholder="e.g. Ground, 1st"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground font-medium">
              Total Area (sq.ft)
            </label>
            <Input
              type="number"
              min={0}
              value={form.sqftArea}
              onChange={(e) =>
                set(
                  "sqftArea",
                  e.target.value
                    ? Math.max(0, parseInt(e.target.value, 10) || 0)
                    : "",
                )
              }
              placeholder="e.g. 450"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 items-center pt-1">
          <div className="flex items-center gap-2">
            <Switch
              checked={form.hasLivingRoom}
              onCheckedChange={(v) => set("hasLivingRoom", v)}
              id={`living-${room.id}`}
            />
            <label
              htmlFor={`living-${room.id}`}
              className="text-xs cursor-pointer"
            >
              Hall / Living Room
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={form.hasKitchen}
              onCheckedChange={(v) => set("hasKitchen", v)}
              id={`kitchen-${room.id}`}
            />
            <label
              htmlFor={`kitchen-${room.id}`}
              className="text-xs cursor-pointer"
            >
              Kitchen
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={form.hasSharedKitchen}
              onCheckedChange={(v) => set("hasSharedKitchen", v)}
              id={`shared-kitchen-${room.id}`}
            />
            <label
              htmlFor={`shared-kitchen-${room.id}`}
              className="text-xs cursor-pointer"
            >
              Shared Kitchen
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={form.hasCommonBathroom}
              onCheckedChange={(v) => set("hasCommonBathroom", v)}
              id={`common-bathroom-${room.id}`}
            />
            <label
              htmlFor={`common-bathroom-${room.id}`}
              className="text-xs cursor-pointer"
            >
              Common Bathroom
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={form.hasDiningArea}
              onCheckedChange={(v) => set("hasDiningArea", v)}
              id={`dining-${room.id}`}
            />
            <label
              htmlFor={`dining-${room.id}`}
              className="text-xs cursor-pointer"
            >
              Dining Area
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={form.hasBalcony}
              onCheckedChange={(v) => set("hasBalcony", v)}
              id={`balcony-${room.id}`}
            />
            <label
              htmlFor={`balcony-${room.id}`}
              className="text-xs cursor-pointer"
            >
              Balcony
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={form.hasAc}
              onCheckedChange={(v) => set("hasAc", v)}
              id={`ac-${room.id}`}
            />
            <label htmlFor={`ac-${room.id}`} className="text-xs cursor-pointer">
              Air Conditioned
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={form.hasMassageRoom}
              onCheckedChange={(v) => set("hasMassageRoom", v)}
              id={`massage-${room.id}`}
            />
            <label
              htmlFor={`massage-${room.id}`}
              className="text-xs cursor-pointer"
            >
              Massage Room
            </label>
          </div>
        </div>
      </div>

      <div className="space-y-2 rounded-md border border-border p-3">
        <div className="flex items-center justify-between gap-2">
          <label className="text-xs text-muted-foreground font-medium">
            Duration pricing
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addDurationPrice}
          >
            Add duration
          </Button>
        </div>
        {form.durationPrices.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No duration prices configured yet.
          </p>
        ) : (
          <div className="space-y-3">
            {form.durationPrices.map((item, index) => (
              <div
                key={`${index}-${item.durationNights}`}
                className="grid grid-cols-[80px_1fr_1fr_80px_auto] gap-2 items-end border-b border-border/40 pb-2 last:border-b-0 last:pb-0"
              >
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">
                    Days
                  </label>
                  <Input
                    type="number"
                    min={1}
                    value={item.durationNights}
                    onChange={(e) =>
                      setDurationPrice(
                        index,
                        "durationNights",
                        parseInt(e.target.value, 10) || 1,
                      )
                    }
                    placeholder="7"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">
                    A/C Price (₹)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={item.price}
                    onChange={(e) =>
                      setDurationPrice(
                        index,
                        "price",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">
                    Non-A/C Price (₹)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={item.nonAcPrice ?? 0}
                    onChange={(e) =>
                      setDurationPrice(
                        index,
                        "nonAcPrice",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">
                    Diff
                  </label>
                  <div className="h-9 flex items-center px-1 text-xs font-semibold text-green-600">
                    ₹
                    {(
                      (item.price || 0) - (item.nonAcPrice || 0)
                    ).toLocaleString()}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeDurationPrice(index)}
                  className="mb-0.5"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-xs text-muted-foreground font-medium">
          Description (optional)
        </label>
        <SimpleDescEditor
          value={form.description}
          onChange={(val) => set("description", val)}
          placeholder="Detailed room type description (supports bold *italic* and bullet lists)"
        />
      </div>

      <RoomPhotosPicker
        roomId={room.id}
        businessId={businessId}
        photos={room.photos ?? []}
        businessPhotos={businessPhotos}
      />

      {dirty && (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setForm({
                name: room.name,
                category: room.category ?? RoomCategory.DELUXE,
                layoutType: room.layoutType ?? null,
                occupancy: room.occupancy ?? "",
                style: room.style ?? RoomStyle.PRIVATE,
                bedroomCount: room.bedroomCount ?? 1,
                bathroomCount: room.bathroomCount ?? 1,
                hasLivingRoom: room.hasLivingRoom ?? false,
                hasKitchen: room.hasKitchen ?? false,
                hasSharedKitchen: room.hasSharedKitchen ?? false,
                hasCommonBathroom: room.hasCommonBathroom ?? false,
                hasDiningArea: room.hasDiningArea ?? false,
                hasBalcony: room.hasBalcony ?? false,
                hasAc: room.hasAc,
                hasMassageRoom: room.hasMassageRoom,
                sqftArea: room.sqftArea ?? "",
                floor: room.floor ?? "",
                totalRooms: room.totalRooms,
                description: room.description ?? "",
                durationPrices: getRoomDurationPrices(room),
              });
              setDirty(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onSave}
            disabled={updateMut.isPending}
          >
            {updateMut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
};
